<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import Icon from '../ui/Icon.vue'

/**
 * Modal shell of the library screens: backdrop, title bar, scrolling body, footer. Esc closes (the topmost
 * dialog only), Tab stays inside, focus goes back to where it was.
 */
const props = withDefaults(defineProps<{ title: string; sub?: string | null; width?: number; closeOnBackdrop?: boolean }>(), {
  sub: null,
  width: 720,
  closeOnBackdrop: true,
})
const emit = defineEmits<{ close: [] }>()
const panel = ref<HTMLElement | null>(null)
let returnFocus: HTMLElement | null = null
const stack: HTMLElement[] = ((globalThis as { __libDialogs?: HTMLElement[] }).__libDialogs ??= [])

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function onKey(e: KeyboardEvent): void {
  if (stack[stack.length - 1] !== panel.value) return
  if (e.key === 'Escape') {
    e.preventDefault()
    emit('close')
    return
  }
  if (e.key === 'Tab' && panel.value) {
    const els = [...panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.offsetParent !== null)
    if (!els.length) return
    const first = els[0] as HTMLElement
    const last = els[els.length - 1] as HTMLElement
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

onMounted(() => {
  returnFocus = document.activeElement as HTMLElement | null
  if (panel.value) stack.push(panel.value)
  document.addEventListener('keydown', onKey)
  void nextTick(() => {
    const el = panel.value?.querySelector<HTMLElement>('[autofocus], input, textarea, select') ??
      panel.value?.querySelector<HTMLElement>(FOCUSABLE)
    el?.focus({ preventScroll: true })
  })
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey)
  const i = stack.indexOf(panel.value as HTMLElement)
  if (i >= 0) stack.splice(i, 1)
  returnFocus?.focus?.({ preventScroll: true })
})

function backdrop(): void {
  if (props.closeOnBackdrop) emit('close')
}
</script>

<template>
  <Teleport to="body">
    <div class="ld" @mousedown.self="backdrop">
      <section ref="panel" class="ld-p" role="dialog" aria-modal="true" :aria-label="title" :style="{ width: `${width}px` }">
        <header class="ld-h">
          <div class="ld-ht">
            <h2>{{ title }}</h2>
            <p v-if="sub">{{ sub }}</p>
          </div>
          <button type="button" class="ld-x" aria-label="ปิด" title="ปิด (Esc)" @click="emit('close')"><Icon name="x" /></button>
        </header>
        <div class="ld-b"><slot /></div>
        <footer v-if="$slots.footer" class="ld-f"><slot name="footer" /></footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.ld {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(12, 18, 15, 0.42);
}
.ld-p {
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(12, 18, 15, 0.22);
}
.ld-h {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 12px 20px;
  border-bottom: 1px solid var(--line2);
}
.ld-ht {
  flex: 1;
  min-width: 0;
}
.ld-h h2 {
  font-size: 16px;
  font-weight: 700;
  line-height: 25px;
}
.ld-h p {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.ld-x {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mut);
  flex: none;
}
.ld-x:hover {
  background: #eef2f0;
  color: var(--ink);
}
.ld-b {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-width: thin;
}
.ld-f {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--line2);
}
</style>
